import React, { useEffect, useState } from 'react';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { Button, notification, Radio, Form, Col, Row } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  storeCategory,
  showCategory,
  editCategory,
  updateCategory,
  createCategory,
} from '@app/api/PreClearanceCatergory';
import StatusBar from '@app/components/statusBar/statusBar';
import { notificationController } from '@app/controllers/notificationController';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useTranslation } from 'react-i18next';

interface PreClearanceCategorieFormProps {
  pageTitle: string;
  editable: boolean;
  id: string | null;
  type: 'edit' | 'create' | 'view';
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const PreClearanceCategorieForm: React.FC<PreClearanceCategorieFormProps> = ({
  editable = false,
  id,
  pageTitle,
  type,
  setCurrentBreadcrumName,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const [status, setStatus] = useState<string | null>(null);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const [parentIdOptions, setParentIdOptions] = useState<{ value: number; label: string }[]>([]);
  const navigate = useNavigate();
  const categoryId = parseInt(id ?? '0');

  const setApiData = (res: any) => {
    setCurrentBreadcrumName(res?.pre_clearance_category?.code || 'New');
    form.setFieldsValue({
      code: res?.pre_clearance_category?.code,
      name: res?.pre_clearance_category?.name,
      description: res?.pre_clearance_category?.description,
      status: res?.pre_clearance_category?.status,
      has_letter: res?.pre_clearance_category?.has_letter ?? false,
      has_certificate: res?.pre_clearance_category?.has_certificate ?? false,
      has_annex: res?.pre_clearance_category?.has_annex ?? false,
      parent_id: res?.pre_clearance_category?.parent_id,
      parent_name: res?.pre_clearance_category?.parent?.name ?? 'None',
    });
    setStatus(res.pre_clearance_category.status);
  };

  const setOptions = (res: any) => {
    const mappedStatuses = res.status_options.map((status: any) => ({
      label: status.label,
      value: status.value,
    }));
    setStatusOptions(mappedStatuses);

    const parentCategoryOptions = res.root_options?.map((category: { value: number; label: string }) => ({
      value: category.value,
      label: category.label,
    }));
    setParentIdOptions(parentCategoryOptions || []);
  };

  const getCategory = () => {
    setLoading(true);
    if (categoryId) {
      if (type === 'view') {
        showCategory(categoryId)
          .then((res: any) => {
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to get pre-clearance category', description: err.message });
          });
      } else if (type === 'edit') {
        editCategory(categoryId)
          .then((res: any) => {
            setApiData(res);
            setOptions(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit pre-clearance category', description: err.message });
          });
      }
    } else if (type === 'create') {
      createCategory()
        .then((res: any) => {
          setOptions(res);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: 'Failed to fetch pre-clearance categories', description: err.message });
        });
    }
  };

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={isLoading} onClick={() => form.submit()} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/pre-clearance-categories')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton
          type="primary"
          onClick={() => navigate(`/edit-pre-clearance-category`, { state: { id } })}
          size="small"
        >
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, isLoading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  useEffect(() => {
    getCategory();
  }, [id, setCurrentBreadcrumName]);

  const handleSubmit = (values: any) => {
    if (editable) {
      const submissionData = {
        ...values,
        has_letter: values.has_letter ?? false,
        has_certificate: values.has_certificate ?? false,
        has_annex: values.has_annex ?? false,
      };

      if (type === 'create') {
        storeCategory(submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate('/view-pre-clearance-category', { state: { id: res?.data?.id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
          });
      } else if (type === 'edit' && categoryId) {
        updateCategory(categoryId, submissionData)
          .then(() => {
            setLoading(false);
            navigate('/view-pre-clearance-category', { state: { id: categoryId } });
          })
          .catch((err: any) => {
            notificationController.error({ message: `${err.message}` });
            setLoading(false);
          });
      }
    }
  };

  return (
    <BaseRow gutter={[16, 16]}>
      <BaseCol span={24}>
        <StatusBar
          id={categoryId.toString()}
          status={status}
          setStatus={function (value: React.SetStateAction<string | null>): void {
            throw new Error('Function not implemented.');
          }}
          wfActions={[]}
          currentType={'edit'}
          workflowType={''}
          onViewInvoicesClick={undefined}
        />
      </BaseCol>
      <BaseCol span={24}>
        <BaseCard id="preclearancecategoryForm" title={pageTitle} padding="1.25rem">
          <BaseRow gutter={[16, 16]}>
            <BaseCol span={24}>
              <Form
                form={form}
                name="preclearancecategoryForm"
                labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
                labelAlign="left"
                labelWrap
                wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
                size="small"
                onFinish={handleSubmit}
                style={{ marginLeft: '40px' }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item
                      name="code"
                      label="Item Code"
                      rules={[{ required: true, message: 'Item Code is required' }]}
                    >
                      <BaseInput placeholder="Enter Item Code" readOnly={!editable} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                      <BaseInput placeholder="Enter Name" readOnly={!editable} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item label="Status" name="status" rules={[{ required: true, message: 'Status is required' }]}>
                      {!editable ? (
                        <BaseInput
                          readOnly
                          value={
                            statusOptions.find((option) => option.value === form.getFieldValue('status'))?.label || ''
                          }
                        />
                      ) : (
                        <BaseSelect placeholder="Select Status" disabled={!editable} options={statusOptions} />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item label="Description" name="description" rules={[{ required: false }]}>
                      {!editable ? (
                        <BaseInput readOnly value={form.getFieldValue('description') || ''} />
                      ) : (
                        <BaseInput.TextArea placeholder="Enter Description" disabled={!editable} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item label="Parent Category">
                      {!editable ? (
                        <BaseInput readOnly value={form.getFieldValue('parent_name') || ''} />
                      ) : (
                        <Form.Item name="parent_id" noStyle>
                          <BaseSelect
                            placeholder="Select Parent Category"
                            options={parentIdOptions}
                            disabled={!editable}
                          />
                        </Form.Item>
                      )}
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={24} lg={12}>
                    <Form.Item label="Has Letter" name="has_letter" rules={[{ required: true }]}>
                      <Radio.Group disabled={!editable}>
                        <Radio value={true}>Yes</Radio>
                        <Radio value={false}>No</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item label="Has Certificate" name="has_certificate" rules={[{ required: true }]}>
                      <Radio.Group disabled={!editable}>
                        <Radio value={true}>Yes</Radio>
                        <Radio value={false}>No</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item label="Has Annex" name="has_annex" rules={[{ required: true }]}>
                      <Radio.Group disabled={!editable}>
                        <Radio value={true}>Yes</Radio>
                        <Radio value={false}>No</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </BaseCol>
          </BaseRow>
        </BaseCard>
      </BaseCol>
    </BaseRow>
  );
};

export default PreClearanceCategorieForm;
